import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { of, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, tap } from 'rxjs/operators';
import { SnackBarItem } from 'src/app/shared/global_components/snack-bar/snack-bar-item';
import { BaseClass } from 'src/app/shared/models/base-class';
import { LayoutService } from 'src/app/shared/services/common/layout.service';
import { FileService } from 'src/app/shared/services/rest/file.service';
import { MembersService } from 'src/app/shared/services/rest/members.service';
import { TaskService } from 'src/app/shared/services/rest/task.service';

@Component({
  selector: 'app-ai-settings-card-project',
  templateUrl: './ai-settings-card-project.component.html',
  styleUrls: ['./ai-settings-card-project.component.scss']
})
export class AiSettingsCardProjectComponent extends BaseClass implements OnInit, OnDestroy {
  public aiModelControl: FormControl = new FormControl();
  public models$: ReplaySubject<any> = new ReplaySubject<any>(1);

  public isCreateFramesStart: boolean = false;
  public isCreateFramesHide: boolean = false;
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AiSettingsCardProjectComponent>,
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private sanitizer: DomSanitizer,
    private fileService: FileService,
  ) { super() }

  ngOnInit(): void {
    this.models$.next(this.data.models.slice());

    this.attachSubscriptions(
      this.aiModelControl.valueChanges.pipe(debounceTime(300)).subscribe((resp) => this.onSearchAiModels(resp))
    )
  }

  log() {
    console.log('data', this.data)
  }

  changeWithFrames(e) {
    console.log("changeWithFrames", e)
  }

  understandFileType(val) {
    if (!val) {
      return ""
    }
    
    if (val == 'application/pdf') {
      return 'pdf'
    } else if (val.indexOf("/") != -1) {
      return val.split('/')[0]
    } else {
      return ""
    }
  }

  getModelById(id) {
    if (!this.data.models || this.data.models.filter(el => el.id == id).length == 0) {
      return false;
    }
    return this.data.models.find(el => el.id == id)
  }

  createFrames() {
    if (!this.data.ai_frames.value) {
      this.layoutService.showSnackBar({name: 'You indicated that you want to add frames.'}, marker("But did not select their number"), SnackBarItem);
      return
    }

    this.isCreateFramesStart = true;

    // this.createFramesStart = true;
    
    this.data.cards.forEach(card => {
      card.files.forEach(file => {
        if (file.duration || file.meta_duration) {
          let times = this.getFrameTimes(file.duration || file.meta_duration, this.data.ai_frames.value)
          times.forEach(time => {
            this.addFrame(file, time)
          })
        }
      });
    })

    this.layoutService.showSnackBar({name: 'Frame creation started'}, marker("successfully"), SnackBarItem);

    setTimeout(() => {
      this.isCreateFramesHide = true;
    }, 2000)
  }
  

  addFrame(file, time?) {
    const videoUrl = this.data.host + (file.preview1080 ? file.preview1080 : file.original) + '?company_id=' + this.data.company_id;
    let timeInSeconds;

    if (time) {
      timeInSeconds = time;
    } else {
      timeInSeconds = (document.getElementById(`video_${file.cstID}`) as HTMLVideoElement).currentTime;
    }

    timeInSeconds = +(timeInSeconds as number).toFixed(2)

    this.getVideoFrameAsBlob(videoUrl, timeInSeconds)
      .then((blob) => {
        console.log('Generated Blob:', blob);
        // Например, создаем объект URL для предпросмотра
        const blobUrl = URL.createObjectURL(blob);

        let newFrame = {
          id: this.generateRandomId(),
          blob: blob,
          blobUrl: blobUrl,
          blobSrc: this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl),
          isLoad: true,
          time: timeInSeconds
        }
        
        if (!file.frames) {
          file.frames = [newFrame]
        } else {
          file.frames.push(newFrame)
        }

        this.uploadFrame(file, newFrame);
        console.log('file:', file);
        console.log('Blob URL:', blobUrl);
      })
      .catch((error) => {
        console.log('file:', file);
        console.error('Error:', error);
      });
  }

  uploadFrame(file, frame, is_thumb: boolean = false) {
    this.attachSubscriptions(
      this.fileService.uploadFile({
        company_id: file.company_id,
        task_id: file.task_id,
        task_operation_id: file.task_operation_id,
        filesize: frame.blob.size,
        filename: is_thumb ? `Thumbnail-${(frame.time as number).toFixed(2)}s-${file.filename.split('.')[0]}.png` : `Frame-${(frame.time as number).toFixed(2)}s-${file.filename.split('.')[0]}.png`,
        content_type: frame.blob.type ? frame.blob.type : 'image/png',
        location: is_thumb ? file.location : '/project/ai-resources',
        is_dir: 0
      }, this.data.company.id).pipe(
        tap(res => {
          frame.reportsFile = res;
          frame.place = "cp_master";
          frame.url = window.location.href;
          frame.data = res;
          frame.files = [];
          frame.task = undefined;
          frame.work = undefined;
          frame.activeLang = this.data.activeLang;
          frame.location = is_thumb ? file.location : '/project/ai-resources';
          frame.target = frame.blob;
          frame.user = this.data.user;
          frame.task_id = file.task_id;
          frame.task_operation_id = file.task_operation_id;
          frame.company = this.data.company;
          frame.partition = {
            company_id: file.company_id,
            file_id: file.id,
            consist_of_file_id: frame.reportsFile.id
          };
          frame.company_id = this.data.company.id;
          frame.target_company_id = file.target_company_id;
          this.fileService.files$.next(frame);

          if (is_thumb) {
            file.consists.push(frame);
            file.consists.sort((a, b) => a.time - b.time);
          }
        }),
        catchError((error) => {
          if (error == "Company limit exceeded") {
            this.layoutService.showSnackBar({name: ''}, marker("This company has exceeded the file upload limit."), SnackBarItem)
          } else {
            this.layoutService.showSnackBar({name: ''}, error, SnackBarItem)
          }
          return of(false)
        })
      ).subscribe(resp => {
        console.log(resp)
      })
    )
  }

  generateRandomId(): string {
    const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const length = 10;
    let randomId = '';
  
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      randomId += characters.charAt(randomIndex);
    }
  
    return randomId;
  }

  getVideoFrameAsBlob(videoUrl: string, timeInSeconds: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // Создаем элементы видео и canvas
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        reject('Canvas context not available');
        return;
      }
  
      video.crossOrigin = 'anonymous'; // Устанавливаем для поддержки CORS (если нужно)
      video.src = videoUrl;
  
      // Обработка ошибки загрузки видео
      video.onerror = () => reject('Error loading video');
  
      video.onloadedmetadata = () => {
        // Проверяем, чтобы время не превышало длительность видео
        if (timeInSeconds > video.duration) {
          reject('Specified time exceeds video duration');
          return;
        }
  
        // Устанавливаем время воспроизведения
        video.currentTime = timeInSeconds;
      };
  
      video.onseeked = () => {
        // Устанавливаем размеры canvas
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
  
        // Рисуем кадр на canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
  
        // Генерируем Blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject('Failed to create Blob');
            }
          },
          'image/png', // Формат изображения
          1 // Качество (от 0 до 1)
        );
      };
    });
  }

  getFrameTimes(videoDuration: number, framesCount: number): number[] {
    if (framesCount < 1 || videoDuration <= 0) {
      throw new Error("Неверные параметры: framesCount должен быть >= 1, videoDuration > 0.");
    }
  
    // Определяем количество кусков (framesCount + 2 включает крайние отрезки, которые нужно отбросить)
    const totalSegments = framesCount + 2;
  
    // Длина одного куска
    const segmentDuration = videoDuration / totalSegments;
  
    // Генерируем массив времени начала каждого куска, исключая первый и последний
    const frameTimes = [];
    for (let i = 1; i <= framesCount; i++) {
      frameTimes.push(segmentDuration * i);
    }
  
    return frameTimes;
  }

  icludesVideo() {
    let count = 0
    if (this.data.cards && this.data.cards.length) {
      this.data.cards.forEach(card => {
        card.files.forEach(file => {
          if (this.understandFileType(file.content_type) == 'video') {
            count++;
          }
        })
      })
    }

    return !!count
  }

  onSearchAiModels(resp) {
    if (!this.data.models) {
      return;
    }

    if (!resp) {
      this.models$.next(this.data.models.slice());
      return;
    } else {
      resp = resp.toLowerCase();
    }

    // filter the banks
    this.models$.next(
      this.data.models.filter(b => b.name.toLowerCase().indexOf(resp) > -1)
    );
  }

  close() {
    this.dialogRef.close({event: "close", data: false})
  }

  allFilesUploadFrames() {
    let notUploadedFrames: number = 0;
    this.data.cards.forEach(card => {
      card.files.forEach(file => {
        if (file.frames) {
          file.frames.forEach(frame => {
            if (!frame.uploadedFile) {
              notUploadedFrames++
            }
          });
        } else {
          // if (!!this.ai_frames.value && (!file.frames || !file.frames.length)) {
            
          // }
        }
      });
    });

    return notUploadedFrames == 0;
  }

  generate() {
    if (this.data.file) {
      this.data.isCreateFrames.patchValue(false);
      
      this.dialogRef.close({event: "generate"})
    } else {
      if (!this.allFilesUploadFrames()) {
        this.layoutService.showSnackBar({name: 'Frame creation in progress'}, marker("Wait a few seconds"), SnackBarItem)
        return
      }
  
      this.data.isCreateFrames.patchValue(false);
      
      this.dialogRef.close({event: "generate"})
    }
  }

  ngOnDestroy(): void {
    this.clearSubscriptions()
  }
}
